import {
  getApigeeUrl,
  getEnv,
  getRegion,
  ENVS,
  REGIONS,
} from '@adsk/acs-sc-web-platform-api';
import PermissionSelect from '@adsk/alloy-react-permission-select';
import { SERVICE_ACCESSES } from '@adsk/acs-admin-ui-components-helpers';
import {
  ISSUES_PERMISSION_ACTION,
  ISSUES_PERMISSION_LEVELS,
} from '../../constants';
import { getPillValue } from './utils';

const US_CONFIG = {
  [ENVS.QA]: `${getApigeeUrl()}/construction/issues-qa`,
  [ENVS.STAGING]: `${getApigeeUrl()}/construction/issues-stg`,
  [ENVS.PRODUCTION]: `${getApigeeUrl()}/construction/issues`,
};

const ENV_CONFIGS = {
  [REGIONS.US]: US_CONFIG,
  [REGIONS.EMEA]: US_CONFIG,
  [REGIONS.APAC]: US_CONFIG,
};

export const buildGetIssuesPermissionsRequest = (projectId) => {
  const env = getEnv();
  const region = getRegion();

  return `${ENV_CONFIGS[region][env]}/v1/projects/${projectId}/permissions`;
};

const issuesActionsToLevel = (actions) => {
  if (actions.some(({ name }) => name === ISSUES_PERMISSION_ACTION.WRITE)) {
    return ISSUES_PERMISSION_LEVELS.CREATE;
  }
  if (actions.some(({ name }) => name === ISSUES_PERMISSION_ACTION.READ)) {
    return ISSUES_PERMISSION_LEVELS.EDIT;
  }
  return ISSUES_PERMISSION_LEVELS.VIEW;
};

const getIssuesSelectOptions = (T, overridePermissionInfo) =>
  Object.values(ISSUES_PERMISSION_LEVELS).reduce((result, level) => {
    if (level === ISSUES_PERMISSION_LEVELS.MANAGE) return result;

    const disabled =
      !!overridePermissionInfo && level < overridePermissionInfo.level;
    const pillValue = getPillValue(level);
    const option = {
      label: T.translate(`PERMISSIONS.ISSUES.LEVELS.${level}`),
      value: `header-${level}`,
      pillValue,
      options: [
        {
          value: level,
          label: T.translate(`PERMISSIONS.ISSUES.LEVEL_DESCRIPTIONS.${level}`),
          pillValue,
          disabled,
          tooltip:
            disabled && !!overridePermissionInfo
              ? T.translate('PERMISSIONS.ISSUES.DISABLED_DESCRIPTION', {
                  level: overridePermissionInfo.levelName,
                  subjectType: overridePermissionInfo.subjectType,
                  subjectName: overridePermissionInfo.subjectName,
                })
              : T.translate(`PERMISSIONS.ISSUES.LEVEL_DESCRIPTIONS.${level}`),
        },
      ],
    };
    result.push(option);

    return result;
  }, []);

const getLevelLabel = (level, T) =>
  T.translate(`PERMISSIONS.ISSUES.LEVELS.${level}`);

const getIssuesInheritedPermissionData = (
  memberPermissionLevel,
  companyPermissionsLevel,
  rolePermissionsLevel,
  selectedCompanyName,
  selectedRoleName,
  selectedAccessLevel,
  T
) => {
  if (selectedAccessLevel === SERVICE_ACCESSES.ADMIN) {
    return {
      level: ISSUES_PERMISSION_LEVELS.MANAGE,
      levelName: T.translate(`PERMISSIONS.ISSUES.LEVELS.4`),
      subjectType: T.translate('PERMISSIONS.TYPES.ROLE_TEXT'),
      subjectName: 'Admin',
    };
  }

  let level = memberPermissionLevel;
  let subjectName;
  let subjectType;

  if (companyPermissionsLevel > level) {
    level = companyPermissionsLevel;
    subjectType = T.translate('PERMISSIONS.TYPES.COMPANY_TEXT');
    subjectName = selectedCompanyName;
  }

  if (rolePermissionsLevel > level) {
    level = rolePermissionsLevel;
    subjectType = T.translate('PERMISSIONS.TYPES.ROLE_TEXT');
    subjectName = selectedRoleName;
  }

  return level !== memberPermissionLevel
    ? {
        level,
        levelName: getLevelLabel(level, T),
        subjectType,
        subjectName,
      }
    : null;
};

const getRolePermissions = (rolesPermissions, rolesInfo) => {
  let higherPermissions;
  let roleName;

  rolesPermissions.forEach((rolePermissions) => {
    if (
      !higherPermissions ||
      (higherPermissions &&
        issuesActionsToLevel(rolePermissions.actions) >
          issuesActionsToLevel(higherPermissions.actions))
    ) {
      higherPermissions = rolePermissions;
      roleName = rolesInfo.find(
        (e) => e.memberGroupId === rolePermissions.subject
      ).name;
    }
  });

  return {
    rolePermissionLevel: issuesActionsToLevel(higherPermissions.actions),
    roleName,
  };
};

export const getIssuesPermissionsInfo = (
  memberPermissions,
  companyPermissions,
  rolesPermissions,
  selectedAccessLevel,
  selectedCompany,
  selectedRoles,
  T
) => {
  const memberPermissionLevel = issuesActionsToLevel(
    memberPermissions?.actions || []
  );
  const companyPermissionLevel = companyPermissions
    ? issuesActionsToLevel(companyPermissions.actions)
    : 0;
  const { rolePermissionLevel, roleName } =
    rolesPermissions.length > 0
      ? getRolePermissions(rolesPermissions, selectedRoles)
      : { rolePermissionLevel: 0 };
  const inheritedPermissionData = getIssuesInheritedPermissionData(
    memberPermissionLevel,
    companyPermissionLevel,
    rolePermissionLevel,
    selectedCompany?.name,
    roleName,
    selectedAccessLevel,
    T
  );
  const inheritedPermissions = !!inheritedPermissionData;
  let inheritanceInfo;
  let permissionsIndicatorProps;
  if (inheritedPermissions) {
    const { level, levelName, subjectType, subjectName } =
      inheritedPermissionData;
    inheritanceInfo = T.translate('INHERITED_PERMISSIONS_INFO', {
      subjectType,
      subjectName,
    });
    permissionsIndicatorProps = {
      label: levelName,
      pillValue: getPillValue(level),
    };
  }

  return {
    selectValue: memberPermissionLevel && { value: memberPermissionLevel },
    permissionsIndicatorProps,
    inheritedPermissions,
    inheritanceInfo,
    selectOptions: getIssuesSelectOptions(T, inheritedPermissionData),
    selectType: PermissionSelect.displayName,
    selectValueLabel: getLevelLabel(memberPermissionLevel, T),
    pillValue: getPillValue(memberPermissionLevel),
  };
};
